/** Une période absente reste inconnue : elle ne contribue jamais comme un zéro. */
export interface RemunerationMonth {
  readonly period: string;
  readonly takenCents: bigint | null;
  readonly ingoingsCents: bigint | null;
  readonly outgoingsCents: bigint | null;
  readonly bankCents: bigint | null;
  readonly bankDate: string | null;
  readonly calculatedBankCents?: bigint | null;
  readonly calculatedBankDate?: string | null;
  readonly bankAnchorDate?: string | null;
  readonly clientReceiptsCents?: bigint | null;
  readonly expensesComplete?: boolean;
  readonly revenue?: {
    readonly htCents: bigint | null;
    readonly ttcCents: bigint | null;
    readonly vatCents: bigint | null;
    readonly complete: boolean;
  };
  readonly clientRevenues?: readonly {
    readonly sourceConnectionId: string;
    readonly sourceClientId: string;
    readonly name: string;
    readonly receivedHtCents: bigint | null;
    readonly complete: boolean;
  }[];
  readonly expenses?: readonly {
    readonly sourceConnectionId: string;
    readonly categoryId: string;
    readonly name: string;
    readonly paidCents: bigint;
    readonly htCents: bigint | null;
  }[];
}

/** Projection du réalisé importé, distincte d'une prévision et d'une clôture comptable. */
export class RemunerationProjection {
  public constructor(public readonly months: readonly RemunerationMonth[]) {}

  /** Deux mois de sorties usuelles, observées sur trois mois consécutifs, hors prélèvements. */
  public reserveSuggestionCents(through: string): bigint | null {
    let sum = 0n;
    for (let offset = 1; offset <= 3; offset += 1) {
      const period = new Date(
        Date.UTC(Number(through.slice(0, 4)), Number(through.slice(5)) - 1 - offset, 1),
      )
        .toISOString()
        .slice(0, 7);
      const row = this.months.find((entry) => entry.period === period);
      if (
        !row ||
        row.outgoingsCents === null ||
        row.takenCents === null ||
        row.outgoingsCents < row.takenCents
      )
        return null;
      sum += row.outgoingsCents - row.takenCents;
    }
    return (sum * 2n) / 3n;
  }

  public annual(
    year: number,
    through: string,
  ): {
    year: number;
    totalCents: bigint | null;
    averageCents: bigint | null;
    knownMonths: number;
    activeMonths: number;
    retainedCents: bigint | null;
    knownFlowMonths: number;
  } {
    const startMonth = year === 2020 ? 9 : 1;
    const endMonth = year === Number(through.slice(0, 4)) ? Number(through.slice(5)) : 12;
    const rows = this.months.filter(
      (row) =>
        row.period >= `${year}-${String(startMonth).padStart(2, '0')}` &&
        row.period.startsWith(`${year}-`) &&
        row.period <= through,
    );
    const known = rows.filter((row) => row.takenCents !== null);
    const total = known.reduce((sum, row) => sum + row.takenCents!, 0n);
    const flows = rows.filter((row) => row.ingoingsCents !== null && row.outgoingsCents !== null);
    const activeMonths = Math.max(0, endMonth - startMonth + 1);
    return {
      year,
      totalCents: known.length === 0 ? null : total,
      averageCents:
        known.length === 0
          ? null
          : (total + (total < 0n ? -1n : 1n) * (BigInt(known.length) / 2n)) / BigInt(known.length),
      knownMonths: known.length,
      activeMonths,
      knownFlowMonths: flows.length,
      retainedCents:
        flows.length === activeMonths && activeMonths > 0
          ? flows.reduce((sum, row) => sum + row.ingoingsCents! - row.outgoingsCents!, 0n)
          : null,
    };
  }

  /**
   * Repère prudent, pas une promesse de revenu : le supplément est limité à la fois au
   * flux net observé et au solde daté après réserve et obligations RESTANT à régler.
   * Un montant déclaré ne constitue pas la preuve d'une dette restant à payer.
   * Un ancien solde ne permet pas de conseiller un nouveau versement dans un autre mois.
   */
  public advice(
    period: string,
    reserve: bigint | null,
    goal: bigint | null,
    remainingObligations: bigint | null,
    obligationsComplete = true,
  ): {
    advisedCents: bigint | null;
    additionalCents: bigint | null;
    retainedCents: bigint | null;
  } {
    const row = this.months.find((month) => month.period === period);
    const retained =
      row?.ingoingsCents !== null && row?.ingoingsCents !== undefined && row.outgoingsCents !== null
        ? row.ingoingsCents - row.outgoingsCents
        : null;
    // L'objectif déjà atteint suffit à démontrer un supplément nul, même si
    // la banque ou certaines obligations manquent. Cela ne valide ni le
    // prélèvement passé ni une disponibilité de trésorerie supplémentaire.
    if (row?.takenCents != null && goal !== null && row.takenCents >= goal)
      return { advisedCents: row.takenCents, additionalCents: 0n, retainedCents: retained };
    if (
      !row ||
      row.takenCents === null ||
      retained === null ||
      row.bankCents === null ||
      row.bankDate?.slice(0, 7) !== period ||
      reserve === null ||
      goal === null ||
      remainingObligations === null ||
      remainingObligations < 0n
    ) {
      return { advisedCents: null, additionalCents: null, retainedCents: retained };
    }
    const cashRoom = max(0n, row.bankCents - reserve - remainingObligations);
    const flowRoom = max(0n, retained);
    // Même avec un inventaire incomplet, un plafond déjà nul prouve qu'aucun
    // supplément n'est prudent. Une dette inconnue ne peut qu'abaisser ce plafond.
    if (!obligationsComplete && cashRoom > 0n && flowRoom > 0n && goal > row.takenCents)
      return { advisedCents: null, additionalCents: null, retainedCents: retained };
    const additional = min(max(0n, goal - row.takenCents), min(cashRoom, flowRoom));
    return {
      advisedCents: row.takenCents + additional,
      additionalCents: additional,
      retainedCents: retained,
    };
  }
}

const min = (a: bigint, b: bigint): bigint => (a < b ? a : b);
const max = (a: bigint, b: bigint): bigint => (a > b ? a : b);
